import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  List,
  LayoutGrid,
  Briefcase,
  Scale,
  GitCompare,
  Lightbulb,
  Clock,
  Wallet,
  Bell,
  MessageSquare,
  Workflow,
  Store,
  Users,
  FileSearch,
  FileSpreadsheet,
  TextSearch,
  PieChart,
  Bot,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { BRAND } from '@/config/brand';
import { cn } from '@/utils/cn';
import { buildWorkspaceNavTabs } from '@/api/workspaceScope';
import { appendSapyonAgentTab } from '@/components/layout/sapyonAgentNav';

const KEYWORD_OPTIMIZATION_ID = 'keyword-optimization';
const KEYWORDS_HASH = `#${KEYWORD_OPTIMIZATION_ID}`;

function isRecommendationsPath(pathname: string): boolean {
  return /\/campaigns\/recommendations\/?$/.test(pathname);
}

function scrollRecommendationsToTop() {
  const main = document.querySelector('main.custom-scrollbar');
  if (main instanceof HTMLElement) {
    main.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function scrollToKeywordOptimization() {
  document.getElementById(KEYWORD_OPTIMIZATION_ID)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
}

const NAV_ICONS: Record<string, LucideIcon> = {
  Dashboard: LayoutDashboard,
  Campaigns: List,
  Report: FileSpreadsheet,
  Overview: LayoutGrid,
  Workspace: Briefcase,
  Analysis: FileSearch,
  'N-word': TextSearch,
  'Share of voice': PieChart,
  'Budget rules': Scale,
  Automations: Workflow,
  Compare: GitCompare,
  Recommendations: Lightbulb,
  'Daily spend': Wallet,
  'Budget usage': Bell,
  Dayparting: Clock,
  Chat: MessageSquare,
  'Sapyon Agent': Bot,
  Audience: Users,
  Retail: Store,
  Vendor: Store,
};

function userInitials(email: string | undefined): string {
  if (!email) return '?';
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return local.slice(0, 2).toUpperCase() || '?';
}

type SidebarProps = {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
};

export function Sidebar({ expanded, onExpandedChange }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const [recsMenuOpen, setRecsMenuOpen] = useState(false);
  const recsCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openRecsMenu = () => {
    if (recsCloseTimerRef.current) {
      clearTimeout(recsCloseTimerRef.current);
      recsCloseTimerRef.current = null;
    }
    setRecsMenuOpen(true);
  };

  const closeRecsMenu = () => {
    recsCloseTimerRef.current = setTimeout(() => {
      setRecsMenuOpen(false);
      recsCloseTimerRef.current = null;
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (recsCloseTimerRef.current) clearTimeout(recsCloseTimerRef.current);
    };
  }, []);

  const tabs = appendSapyonAgentTab(
    buildWorkspaceNavTabs(location.pathname),
    location.pathname,
    isSuperAdmin
  );

  const collapseAndBlur = () => {
    onExpandedChange(false);
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('aside')) {
      active.blur();
    }
  };

  return (
    <aside
      onMouseEnter={() => onExpandedChange(true)}
      onMouseLeave={collapseAndBlur}
      className={cn(
        'fixed left-0 top-0 z-50 flex h-full flex-col overflow-hidden bg-chrome py-4',
        'transition-[width,box-shadow] duration-200 ease-out',
        expanded ? 'w-[220px] shadow-lg' : 'w-[68px]'
      )}
    >
      {/* Brand: icon centered in rail when collapsed */}
      <div
        className={cn(
          'mb-3.5 flex h-11 shrink-0 items-center',
          expanded ? 'gap-3 px-4' : 'justify-center'
        )}
      >
        <img
          src="/sapyon-mark-reversed.svg"
          alt={BRAND.productName}
          title={BRAND.productName}
          className="h-[34px] w-[34px] shrink-0 object-contain"
        />
        {expanded ? (
          <div className="min-w-0 overflow-hidden whitespace-nowrap">
            <p className="truncate font-display text-sm font-semibold text-chrome-text">{BRAND.productName}</p>
            {BRAND.productTagline ? (
              <p className="truncate text-[10px] font-semibold uppercase tracking-wider text-chrome-muted">
                {BRAND.productTagline}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Nav: 12px inset so 44px hit targets are centered in 68px rail */}
      <nav className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-x-hidden overflow-y-auto overscroll-y-contain px-3">
        {tabs.map((tab) => {
          const Icon = NAV_ICONS[tab.label] ?? LayoutDashboard;
          const navClass = ({ isActive }: { isActive: boolean }) =>
            cn(
              'flex h-11 w-full items-center rounded-xl transition-colors duration-200',
              expanded ? 'justify-start gap-3 px-3' : 'justify-center',
              isActive
                ? 'sidebar-active'
                : 'text-chrome-muted hover:bg-white/[0.09] hover:text-chrome-text'
            );

          if (tab.label !== 'Recommendations') {
            return (
              <NavLink
                key={tab.to + tab.label}
                to={tab.to}
                end={tab.end}
                title={tab.label}
                aria-label={tab.label}
                onClick={(e) => {
                  (e.currentTarget as HTMLElement).blur();
                }}
                className={navClass}
              >
                <Icon className="h-[19px] w-[19px] shrink-0" strokeWidth={1.5} aria-hidden />
                {expanded ? (
                  <span className="truncate text-body-md font-medium">{tab.label}</span>
                ) : null}
              </NavLink>
            );
          }

          const onRecsPage = isRecommendationsPath(location.pathname);
          const keywordsActive = onRecsPage && location.hash === KEYWORDS_HASH;
          const campaignsActive = onRecsPage && !keywordsActive;
          const recsSearch = onRecsPage ? location.search : '';
          const goToCampaigns = () => {
            if (onRecsPage && !keywordsActive) {
              scrollRecommendationsToTop();
              return;
            }
            navigate({ pathname: tab.to, search: recsSearch });
          };
          const goToKeywords = () => {
            if (onRecsPage && keywordsActive) {
              scrollToKeywordOptimization();
              return;
            }
            navigate({ pathname: tab.to, search: recsSearch, hash: KEYWORDS_HASH });
          };

          const recsItemClass = (isActive: boolean) =>
            cn(
              'flex h-9 w-full items-center rounded-lg px-3 text-left text-body-md font-medium transition-colors duration-200',
              isActive
                ? 'sidebar-active'
                : 'text-chrome-muted hover:bg-white/[0.09] hover:text-chrome-text'
            );

          return (
            <div
              key={tab.to + tab.label}
              onMouseEnter={openRecsMenu}
              onMouseLeave={closeRecsMenu}
            >
              <NavLink
                to={tab.to}
                end={tab.end}
                title={tab.label}
                aria-label={tab.label}
                aria-haspopup="menu"
                aria-expanded={recsMenuOpen}
                onClick={(e) => {
                  (e.currentTarget as HTMLElement).blur();
                }}
                className={navClass}
              >
                <Icon className="h-[19px] w-[19px] shrink-0" strokeWidth={1.5} aria-hidden />
                {expanded ? (
                  <span className="truncate text-body-md font-medium">{tab.label}</span>
                ) : null}
              </NavLink>
              {expanded && recsMenuOpen ? (
                <div
                  role="menu"
                  aria-label="Recommendations"
                  className="mt-0.5 ml-[44px] border-l border-white/15 py-0.5 pl-1.5"
                >
                  <button
                    type="button"
                    role="menuitem"
                    aria-current={campaignsActive ? 'page' : undefined}
                    className={recsItemClass(campaignsActive)}
                    onClick={goToCampaigns}
                  >
                    Campaigns
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    aria-current={keywordsActive ? 'page' : undefined}
                    className={recsItemClass(keywordsActive)}
                    onClick={goToKeywords}
                  >
                    Keywords
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>

      {/* Footer: same 12px inset / centered avatar when collapsed */}
      <div className="mt-auto shrink-0 px-3 pt-3">
        {user?.is_demo ? (
          <div
            className={cn(
              'flex h-11 w-full items-center rounded-xl',
              expanded ? 'gap-3 px-3' : 'justify-center'
            )}
            title={user?.email ?? undefined}
          >
            <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-brand-deep text-xs font-semibold text-chrome-text">
              {userInitials(user?.email ?? undefined)}
            </div>
            {expanded ? (
              <p className="min-w-0 truncate text-xs font-semibold text-chrome-text" title={user?.email ?? undefined}>
                {user?.email ?? '—'}
              </p>
            ) : null}
          </div>
        ) : (
          <NavLink
            to="/settings"
            title="Settings"
            aria-label="Settings"
            onClick={(e) => {
              (e.currentTarget as HTMLElement).blur();
            }}
            className={({ isActive }) =>
              cn(
                'flex h-11 w-full items-center rounded-xl transition-colors duration-200',
                expanded ? 'gap-3 px-3' : 'justify-center',
                isActive ? 'bg-white/10' : 'hover:bg-white/[0.09]'
              )
            }
          >
            <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-brand-deep text-xs font-semibold text-chrome-text">
              {userInitials(user?.email ?? undefined)}
            </div>
            {expanded ? (
              <div className="min-w-0 overflow-hidden">
                <p className="truncate text-xs font-semibold text-chrome-text" title={user?.email ?? undefined}>
                  {user?.email ?? '—'}
                </p>
                <p className="truncate text-[10px] text-chrome-muted">Settings</p>
              </div>
            ) : null}
          </NavLink>
        )}
      </div>
    </aside>
  );
}
